import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RoleGuard } from "@/context/RoleContext";
import { fmtDate } from "@/components/grc/shared";
import { UploadEvidenceModal } from "@/components/grc/UploadEvidenceModal";
import { EvidencePreviewModal } from "@/components/grc/EvidencePreviewModal";
import { VersionsDialog, NewVersionDialog, DeleteEvidenceDialog, RecycleBin, downloadBlob } from "@/components/grc/EvidenceLifecycle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Upload, Download, Trash2, Eye, FileText, FileImage, FileSpreadsheet, FileJson, File as FileIcon,
  History, FilePlus2, Archive, Recycle,
} from "lucide-react";

const EXPIRY_PILL = {
  Valid: "bg-green-100 text-green-700",
  "Expiring Soon": "bg-yellow-100 text-yellow-800",
  Expired: "bg-red-100 text-red-700",
  "No Expiry": "bg-slate-100 text-slate-500",
};

export const fileExt = (name) => (name && name.includes(".") ? name.split(".").pop().toLowerCase() : "");

const iconFor = (name) => {
  const ext = fileExt(name);
  if (["png", "jpg", "jpeg"].includes(ext)) return FileImage;
  if (["xlsx", "csv"].includes(ext)) return FileSpreadsheet;
  if (ext === "json") return FileJson;
  if (["pdf", "docx", "txt"].includes(ext)) return FileText;
  return FileIcon;
};

export const formatBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const EvidenceVault = ({ assessmentId }) => {
  const [canManage, setCanManage] = useState(false);
  const [integrity, setIntegrity] = useState({ visible: false, issues: 0 });
  const [onlyModified, setOnlyModified] = useState(false);
  const [showBin, setShowBin] = useState(false);
  const [historyOf, setHistoryOf] = useState(null);
  const [versionOf, setVersionOf] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [evidence, setEvidence] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [controlFilter, setControlFilter] = useState("all");
  const [expiryFilter, setExpiryFilter] = useState("all");
  const [tagSearch, setTagSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [storageBackend, setStorageBackend] = useState(null);
  const [orgName, setOrgName] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const fetchEvidence = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/grc/${assessmentId}/evidence`);
      setEvidence(data.evidence || []);
      setStorageBackend(data.storage_backend || null);
      setCanManage(!!data.can_manage);
      setIntegrity({ visible: !!data.can_view_integrity, issues: data.integrity_issues || 0 });
    } catch {
      setEvidence([]);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    fetchEvidence();
    api.get(`/grc/${assessmentId}/controls`).then(({ data }) => setControls(data.controls || [])).catch(() => {});
    api.get("/platform/tenant").then(({ data }) => setOrgName(data?.name || null)).catch(() => {});
  }, [assessmentId, fetchEvidence]);

  const filtered = useMemo(
    () =>
      evidence.filter((e) => {
        if (controlFilter !== "all" && e.control_id !== controlFilter) return false;
        if (expiryFilter !== "all") {
          if (expiryFilter === "Expiring" && e.expiry_status !== "Expiring Soon") return false;
          if (expiryFilter !== "Expiring" && e.expiry_status !== expiryFilter) return false;
        }
        if (tagSearch && !(e.tags || []).some((t) => t.toLowerCase().includes(tagSearch.toLowerCase()))) return false;
        if (onlyModified && e.integrity_status !== "modified_out_of_band") return false;
        return true;
      }),
    [evidence, controlFilter, expiryFilter, tagSearch, onlyModified]
  );

  const download = (item) => downloadBlob(`/grc/evidence/${item.file_id}/download`, item.filename).catch(() => toast.error("Download failed"));

  const exportZip = async () => {
    setExporting(true);
    try {
      await downloadBlob(`/grc/${assessmentId}/evidence/export.zip`, `evidence-${String(assessmentId).slice(0, 8)}.zip`);
      toast.success("Evidence ZIP downloaded");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "You don't have permission to export evidence" : "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const closeHistory = useCallback(() => setHistoryOf(null), []);
  const previewable = (name) => ["png", "jpg", "jpeg", "pdf"].includes(fileExt(name));

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 space-y-4" data-testid="evidence-vault">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={controlFilter} onValueChange={setControlFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="evidence-control-filter">
            <SelectValue placeholder="Control" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Controls</SelectItem>
            {controls.map((c) => (
              <SelectItem key={c.control_id} value={c.control_id}>
                {c.question_id} · {c.pillar}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={expiryFilter} onValueChange={setExpiryFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="evidence-expiry-filter">
            <SelectValue placeholder="Expiry" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="Valid">Valid</SelectItem>
            <SelectItem value="Expiring">Expiring Soon</SelectItem>
            <SelectItem value="Expired">Expired</SelectItem>
            <SelectItem value="No Expiry">No Expiry</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={tagSearch}
          onChange={(e) => setTagSearch(e.target.value)}
          placeholder="Search tags…"
          className="w-[160px] h-8 text-xs"
          data-testid="evidence-tag-search"
        />
        {integrity.visible && (
          <Button variant={onlyModified ? "default" : "outline"} onClick={() => setOnlyModified(!onlyModified)}
            className={`h-8 text-xs ${integrity.issues ? "border-amber-400 text-amber-700" : ""} ${onlyModified ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
            data-testid="evidence-integrity-filter">
            Integrity: {integrity.issues} {integrity.issues === 1 ? "issue" : "issues"}
          </Button>
        )}
        <Button variant="outline" onClick={exportZip} disabled={exporting} className="ml-auto h-8 text-xs" data-testid="evidence-export-zip">
          <Archive size={14} className="mr-1.5" /> {exporting ? "Preparing ZIP…" : "Export ZIP"}
        </Button>
        {canManage && (
          <Button variant={showBin ? "default" : "outline"} onClick={() => setShowBin(!showBin)} className="h-8 text-xs" data-testid="evidence-recycle-toggle">
            <Recycle size={14} className="mr-1.5" /> {showBin ? "Back to vault" : "Recycle bin"}
          </Button>
        )}
        <RoleGuard requiredRole="control_owner">
          <Button
            onClick={() => setUploadOpen(true)}
            className="h-8 bg-[#008080] hover:bg-[#1a51db] text-xs"
            data-testid="evidence-upload-button"
          >
            <Upload size={14} className="mr-1.5" /> Upload
          </Button>
        </RoleGuard>
      </div>

      {showBin ? (
        <RecycleBin assessmentId={assessmentId} onRestored={fetchEvidence} />
      ) : loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-500" data-testid="evidence-empty-state">
          {evidence.length === 0
            ? "No evidence uploaded yet. Click Upload to add your first document."
            : "No evidence matches your filters"}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="evidence-grid">
          {filtered.map((item) => {
            const Icon = iconFor(item.filename);
            return (
              <div
                key={item.file_id}
                className="border border-slate-200 rounded-xl p-4 space-y-2 hover:shadow-md transition-shadow"
                data-testid={`evidence-card-${item.file_id}`}
              >
                <div className="flex items-start justify-between">
                  <Icon size={26} className="text-[#008080]" />
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${EXPIRY_PILL[item.expiry_status] || EXPIRY_PILL["No Expiry"]}`}>
                    {item.expiry_status}
                  </span>
                </div>
                <div className="text-xs font-medium text-slate-800 truncate" title={item.filename}>
                  {item.filename}
                  {item.integrity_status === "modified_out_of_band" && (
                    <span className="ml-1.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-px text-[10px] font-medium"
                      title={`Modified outside the app: the stored copy no longer matches its fingerprint (detected ${fmtDate(item.integrity_detected_at)}). Last verified ${item.last_verified_at ? fmtDate(item.last_verified_at) : "never"}.`}
                      data-testid={`evidence-integrity-badge-${item.file_id}`}>
                      Modified
                    </span>
                  )}
                  {item.version_no > 1 && (
                    <span className="ml-1.5 rounded-full bg-[#003366]/10 text-[#003366] px-1.5 py-px text-[10px]" data-testid={`evidence-version-chip-${item.file_id}`}>
                      v{item.version_no}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {item.control_title && (
                    <span className="px-1.5 py-0.5 rounded border border-slate-300 text-[10px] font-mono text-slate-600">
                      {item.control_title}
                    </span>
                  )}
                  {item.pillar && (
                    <span className="px-1.5 py-0.5 rounded border border-slate-300 text-[10px] font-mono text-slate-600">
                      {item.pillar}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-slate-400">
                  {fmtDate(item.upload_date)} · {formatBytes(item.size)}
                  {item.expiry_date && <> · expires {fmtDate(item.expiry_date)}</>}
                </div>
                {(item.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {item.tags.map((t) => (
                      <span key={t} className="px-1.5 py-0.5 rounded-full bg-slate-100 text-[9px] text-slate-600">
                        {t}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-1 pt-1 border-t border-slate-100">
                  {previewable(item.filename) && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500"
                      onClick={() => setPreview(item)} data-testid={`evidence-preview-${item.file_id}`}>
                      <Eye size={13} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500"
                    onClick={() => download(item)} data-testid={`evidence-download-${item.file_id}`}>
                    <Download size={13} />
                  </Button>
                  {item.storage_backend === "sharepoint" && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500" title="Version history"
                      onClick={() => setHistoryOf(item)} data-testid={`evidence-history-${item.file_id}`}>
                      <History size={13} />
                    </Button>
                  )}
                  {item.storage_backend === "sharepoint" && (
                    <RoleGuard requiredRole="control_owner">
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500" title="Upload new version"
                        onClick={() => setVersionOf(item)} data-testid={`evidence-new-version-${item.file_id}`}>
                        <FilePlus2 size={13} />
                      </Button>
                    </RoleGuard>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500 ml-auto" title="Delete"
                      onClick={() => setDeleting(item)} data-testid={`evidence-delete-${item.file_id}`}>
                      <Trash2 size={13} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <UploadEvidenceModal
        assessmentId={assessmentId}
        controls={controls}
        open={uploadOpen}
        storageBackend={storageBackend}
        orgName={orgName}
        onClose={() => setUploadOpen(false)}
        onUploaded={() => {
          setUploadOpen(false);
          fetchEvidence();
        }}
      />
      <EvidencePreviewModal item={preview} open={!!preview} onClose={() => setPreview(null)} />

      <DeleteEvidenceDialog item={deleting} onClose={() => setDeleting(null)} onDone={() => { setDeleting(null); fetchEvidence(); }} />
      <VersionsDialog item={historyOf} onClose={closeHistory} />
      <NewVersionDialog item={versionOf} onClose={() => setVersionOf(null)} onDone={() => { setVersionOf(null); fetchEvidence(); }} />
    </div>
  );
};
